/**
 * CC Marketing: Admin Blog CMS Service
 * Frontend API service for blog post management, media, templates, and agent keys.
 * Follows the same pattern as adminCampaignService.ts.
 */

import { storage } from './storage';
import { API_BASE_URL } from '../utils/apiConfig';

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content_html: string;
  content_json: any;
  author: string;
  author_id: string | null;
  category: string;
  hero_image_url: string | null;
  read_time: string | null;
  tags: string[];
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  publish_at: string | null;
  published_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  // Social sharing fields
  social_title: string | null;
  social_description: string | null;
  social_image_url: string | null;
  social_image_alt: string | null;
  twitter_card_type: string | null;
  twitter_title: string | null;
  twitter_description: string | null;
  twitter_image_url: string | null;
  twitter_image_alt: string | null;
  pinterest_image_url: string | null;
  canonical_url_override: string | null;
  noindex: boolean;
  created_at: string;
  updated_at: string;
}

export interface BlogAuthor {
  id: string;
  name: string;
  slug: string;
  bio: string | null;
  avatar_url: string | null;
  url: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlogTemplate {
  id: string;
  name: string;
  description: string;
  content_html: string;
  content_json: any;
  category: string | null;
  created_at: string;
}

export interface BlogMedia {
  id: string;
  filename: string;
  original_filename: string | null;
  s3_key: string;
  url: string;
  content_type: string | null;
  size_bytes: number | null;
  width: number | null;
  height: number | null;
  alt_text: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  color: string;
  sort_order: number;
  created_at: string;
}

export interface BlogRevision {
  id: string;
  post_id: string;
  title: string | null;
  content_html: string;
  content_json: any;
  revised_by: string | null;
  created_at: string;
}

export interface AgentApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  is_active: boolean;
  last_used_at: string | null;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  api_key?: string; // Only present on creation
}

export class AdminBlogService {
  private static async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await storage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      'X-Admin-Client': 'firebase-portal',
      'Authorization': `Bearer ${token}`,
    };
  }

  private static async getFetchOptions(method: string, body?: any): Promise<RequestInit> {
    return {
      method,
      headers: await this.getAuthHeaders(),
      credentials: 'include' as RequestCredentials,
      body: body ? JSON.stringify(body) : undefined,
    };
  }

  // ── Posts ──

  static async listPosts(filters?: {
    status?: string;
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ posts: BlogPost[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.category) params.set('category', filters.category);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));

    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts${qs}`,
      await this.getFetchOptions('GET'),
    );
    if (!res.ok) throw new Error(`Failed to list posts: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async getPost(postId: string): Promise<BlogPost> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}`,
      await this.getFetchOptions('GET'),
    );
    if (!res.ok) throw new Error(`Failed to get post: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async createPost(data: Partial<BlogPost> & { template_id?: string }): Promise<BlogPost> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts`,
      await this.getFetchOptions('POST', data),
    );
    if (!res.ok) throw new Error(`Failed to create post: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async updatePost(postId: string, data: Partial<BlogPost>): Promise<BlogPost> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}`,
      await this.getFetchOptions('PUT', data),
    );
    if (!res.ok) throw new Error(`Failed to update post: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async publishPost(postId: string): Promise<BlogPost> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}/publish`,
      await this.getFetchOptions('POST'),
    );
    if (!res.ok) throw new Error(`Failed to publish post: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async schedulePost(postId: string, publishAt: string): Promise<BlogPost> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}/schedule`,
      await this.getFetchOptions('POST', { publish_at: publishAt }),
    );
    if (!res.ok) throw new Error(`Failed to schedule post: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async unpublishPost(postId: string): Promise<BlogPost> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}/unpublish`,
      await this.getFetchOptions('POST'),
    );
    if (!res.ok) throw new Error(`Failed to unpublish post: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async archivePost(postId: string): Promise<BlogPost> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}/archive`,
      await this.getFetchOptions('POST'),
    );
    if (!res.ok) throw new Error(`Failed to archive post: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async deletePost(postId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}`,
      await this.getFetchOptions('DELETE'),
    );
    if (!res.ok) throw new Error(`Failed to delete post: ${res.status}`);
  }

  // ── Categories ──

  static async listCategories(): Promise<BlogCategory[]> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/categories`,
      await this.getFetchOptions('GET'),
    );
    if (!res.ok) throw new Error(`Failed to list categories: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async createCategory(data: { name: string; color?: string }): Promise<BlogCategory> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/categories`,
      await this.getFetchOptions('POST', data),
    );
    if (!res.ok) throw new Error(`Failed to create category: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async updateCategory(categoryId: string, data: { name?: string; color?: string; sort_order?: number }): Promise<BlogCategory> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/categories/${categoryId}`,
      await this.getFetchOptions('PUT', data),
    );
    if (!res.ok) throw new Error(`Failed to update category: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async deleteCategory(categoryId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/categories/${categoryId}`,
      await this.getFetchOptions('DELETE'),
    );
    if (!res.ok) throw new Error(`Failed to delete category: ${res.status}`);
  }

  // ── Revisions ──

  static async listRevisions(postId: string): Promise<BlogRevision[]> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}/revisions`,
      await this.getFetchOptions('GET'),
    );
    if (!res.ok) throw new Error(`Failed to list revisions: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async getRevision(postId: string, revisionId: string): Promise<BlogRevision> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}/revisions/${revisionId}`,
      await this.getFetchOptions('GET'),
    );
    if (!res.ok) throw new Error(`Failed to get revision: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async restoreRevision(postId: string, revisionId: string): Promise<BlogPost> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/posts/${postId}/revisions/${revisionId}/restore`,
      await this.getFetchOptions('POST'),
    );
    if (!res.ok) throw new Error(`Failed to restore revision: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  // ── Templates ──

  static async listTemplates(): Promise<BlogTemplate[]> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/templates`,
      await this.getFetchOptions('GET'),
    );
    if (!res.ok) throw new Error(`Failed to list templates: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async createTemplate(data: Partial<BlogTemplate>): Promise<BlogTemplate> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/templates`,
      await this.getFetchOptions('POST', data),
    );
    if (!res.ok) throw new Error(`Failed to create template: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  // ── Media ──

  static async uploadMedia(file: File, altText?: string): Promise<BlogMedia> {
    const token = await storage.getItem('admin_token');
    const formData = new FormData();
    formData.append('file', file);
    if (altText) formData.append('alt_text', altText);

    const res = await fetch(`${API_BASE_URL}/api/admin/blog/media/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Admin-Client': 'firebase-portal',
      },
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) throw new Error(`Failed to upload media: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async listMedia(limit = 50, offset = 0): Promise<BlogMedia[]> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/media?limit=${limit}&offset=${offset}`,
      await this.getFetchOptions('GET'),
    );
    if (!res.ok) throw new Error(`Failed to list media: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async deleteMedia(mediaId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/media/${mediaId}`,
      await this.getFetchOptions('DELETE'),
    );
    if (!res.ok) throw new Error(`Failed to delete media: ${res.status}`);
  }

  // ── Agent API Keys ──

  static async listAgentKeys(): Promise<AgentApiKey[]> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/agent-keys`,
      await this.getFetchOptions('GET'),
    );
    if (!res.ok) throw new Error(`Failed to list agent keys: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async createAgentKey(data: {
    name: string;
    scopes?: string[];
    expires_at?: string;
  }): Promise<AgentApiKey & { api_key: string }> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/agent-keys`,
      await this.getFetchOptions('POST', data),
    );
    if (!res.ok) throw new Error(`Failed to create agent key: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async revokeAgentKey(keyId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/agent-keys/${keyId}`,
      await this.getFetchOptions('DELETE'),
    );
    if (!res.ok) throw new Error(`Failed to revoke agent key: ${res.status}`);
  }

  // ── Authors ──

  static async listAuthors(): Promise<BlogAuthor[]> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/authors`,
      await this.getFetchOptions('GET'),
    );
    if (!res.ok) throw new Error(`Failed to list authors: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async createAuthor(data: { name: string; bio?: string; avatar_url?: string; url?: string }): Promise<BlogAuthor> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/authors`,
      await this.getFetchOptions('POST', data),
    );
    if (!res.ok) throw new Error(`Failed to create author: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async updateAuthor(authorId: string, data: { name?: string; bio?: string; avatar_url?: string; url?: string }): Promise<BlogAuthor> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/authors/${authorId}`,
      await this.getFetchOptions('PUT', data),
    );
    if (!res.ok) throw new Error(`Failed to update author: ${res.status}`);
    const json = await res.json();
    return json.data;
  }

  static async deleteAuthor(authorId: string): Promise<void> {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/blog/authors/${authorId}`,
      await this.getFetchOptions('DELETE'),
    );
    if (!res.ok) throw new Error(`Failed to delete author: ${res.status}`);
  }

  static async uploadAuthorAvatar(authorId: string, file: File): Promise<BlogAuthor> {
    const token = await storage.getItem('admin_token');
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${API_BASE_URL}/api/admin/blog/authors/${authorId}/avatar`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Admin-Client': 'firebase-portal',
      },
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) throw new Error(`Failed to upload avatar: ${res.status}`);
    const json = await res.json();
    return json.data;
  }
}
